/**
 * Client-side AI facade.
 *
 * This file used to hold a Gemini API key and call the Gemini SDK directly from
 * the browser. Vite inlines any VITE_-prefixed variable, so that key shipped
 * inside the public JavaScript bundle — where it was scraped and abused, and
 * Google suspended the Gemini project. Rotating the key did not help: the
 * replacement shipped publicly too and was suspended within a day.
 *
 * The key now lives only in Cloud Functions. These are thin wrappers over
 * authenticated callables, so nothing secret reaches the browser. The exported
 * signatures are unchanged, so callers did not have to change. Caching moved
 * server-side with the calls (functions/src/aiService.ts), which also means one
 * shared cache instead of one per browser.
 *
 * Do not reintroduce an API key here, or any `VITE_`-prefixed secret anywhere
 * in client code: everything in this bundle is public by definition.
 */
import { getFunctions, httpsCallable } from 'firebase/functions';

/**
 * Extract 3-5 qualitative research tags from a submission's notes.
 * Returns [] on failure — tags are a nice-to-have and must never block the UI.
 */
export const generateTagsForSubmission = async (notes: string): Promise<string[]> => {
    try {
        const call = httpsCallable<{ notes: string }, { tags: string[] }>(
            getFunctions(),
            'generateTagsForNotes',
        );
        const res = await call({ notes });
        return res.data?.tags ?? [];
    } catch (err) {
        console.error('generateTagsForSubmission failed:', err);
        return [];
    }
};

/**
 * Transcribe audio and extract qualitative tags.
 * Throws on failure — the caller surfaces this to the researcher, who
 * explicitly asked for the analysis.
 */
export const generateAudioAnalysis = async (
    base64Audio: string,
    mimeType: string = 'audio/webm',
): Promise<{ transcription: string; tags: string[] }> => {
    const call = httpsCallable<
        { base64Audio: string; mimeType: string },
        { transcription: string; tags: string[] }
    >(getFunctions(), 'analyzeAudio');
    const res = await call({ base64Audio, mimeType });
    return res.data;
};
